import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useCampaignFlow from "../../../../../datahooks/useCampaignFlow";
import { FlowNameSchema } from "../../../../../schemas";

import type { Dispatch, SetStateAction } from "react";
import type { CampaignFlow } from "../../../../../types";

interface FlowActionsDialogProps {
  flow: CampaignFlow;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  type: "delete" | "edit";
  deleteFlow?: (flowId: string) => void;
}

export default function FlowActionsDialog({
  flow,
  isOpen,
  setIsOpen,
  type,
  deleteFlow = null,
}: FlowActionsDialogProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { updateFlow, isUpdatingFlow } = useCampaignFlow();

  const {
    register,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: flow.name,
    },
    resolver: valibotResolver(FlowNameSchema),
  });

  function onSave({ name }: { name: string }) {
    updateFlow({
      flowId: flow.id,
      updates: {
        name,
      },
    }).then(() => {
      reset({ name });
      setIsOpen(false);
    });
  }

  function onConfirm() {
    if (type === "edit") {
      handleSubmit(onSave)();
    } else {
      deleteFlow(flow.id);
      setIsOpen(false);
    }
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Content>
        <Title>
          {type === "edit" ? "Change Flow name" : `Delete ${flow.name}`}
        </Title>

        <Description>
          {type === "edit"
            ? "You can change flow's name here"
            : `Are you sure you want to delete ${flow.name}?`}
        </Description>

        <RenderIf condition={type === "edit"}>
          <FormField
            label="Flow name"
            htmlFor="flow-name-input"
            className="mt-4"
            size="lg"
            error={errors.name?.message}
          >
            <MaxLengthInput
              variant="lg"
              id="flow-name-input"
              characterLength={watch("name").length}
              maxLength={getSchemaMaxLength(FlowNameSchema.entries.name)}
              {...register("name")}
            />
          </FormField>
        </RenderIf>

        <div className="flex mt-8 max-lg:justify-between gap-x-2.5">
          <Button
            className="lg:flex-1"
            variant="secondary-black"
            onClick={() => {
              setIsOpen(false);
              reset();
            }}
          >
            Cancel
          </Button>

          <Button
            isLoading={isUpdatingFlow}
            variant={type === "edit" ? "default-black" : "outline-danger"}
            className="lg:flex-1"
            onClick={onConfirm}
          >
            {type === "edit" ? "Save" : "Delete"}
          </Button>
        </div>
      </Content>
    </Component>
  );
}
